import {
  faArrowLeft,
  faArrowRight,
  faCheckCircle,
  faCircle1,
  faCircle2,
  faCircle3,
  faClock,
  faStopwatch,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Row,
  Col,
  Steps,
  Form,
  Input,
  Select,
  Slider,
  InputNumber,
  Card,
  Typography,
  DatePicker,
  Space,
  Checkbox,
} from "antd";
import { useContext } from "react";
import { useEffect, useState } from "react";
import {
  useCaregiverApplication,
  useCaregiverMutation,
} from "../../api/Caregivers";
import DayAvailability from "../../components/DayAvailability";
import Loading from "../../components/Loading";
import ContentLayout, { ContentTitle } from "../../layout/ContentLayout";
import { AuthContext } from "../../useAuth";
import careCategoryOptions, {
  careCategorySelectOptions,
} from "../../utils/careCategoryOptions";
import { stateList } from "../../utils/stateNames";
import dayjs from "dayjs";
import { MaskedInput } from "antd-mask-input";
import { ageGroupOptions } from "../../utils/ageGroupOptions";

const CaregiverApplication = () => {
  const { user } = useContext(AuthContext);
  const [step, setStep] = useState(0);
  const { isLoading, data } = useCaregiverApplication();
  const [applicationComplete, setApplicationComplete] = useState(false);

  useEffect(() => {
    if (data?.Application) {
      setStep(2);
      setApplicationComplete(true);
    }
  }, [data]);

  const nextStep = () => {
    if (step < steps.length) setStep(step + 1);
  };

  const defaultValues = {
    ...user,
    AgeGroups: [3, 19],
    Sunday: { Enabled: false, Day: 0, valid: true },
    Monday: { Enabled: false, Day: 0, valid: true },
    Tuesday: { Enabled: false, Day: 0, valid: true },
    Wednesday: { Enabled: false, Day: 0, valid: true },
    Thursday: { Enabled: false, Day: 0, valid: true },
    Friday: { Enabled: false, Day: 0, valid: true },
    Saturday: { Enabled: false, Day: 0, valid: true },
  };

  const steps = [
    <ApplicationInfo nextStep={nextStep} />,
    <AppliationWizard
      nextStep={nextStep}
      initialData={data?.Application || defaultValues}
    />,
    <ApplicationComplete />,
  ];

  return isLoading ? (
    <Loading />
  ) : (
    <ContentLayout>
      <ContentTitle>Provider Application</ContentTitle>
      {steps[step]}
    </ContentLayout>
  );
};

const ApplicationComplete = () => {
  return (
    <>
      <Typography.Title level={4}>Thank you!</Typography.Title>
      <Typography.Paragraph>
        Your application has been received and will be reviewed within 48 hours.
        If your application is accepted we will schedule a 15 minute video call
        for screening purposes.
      </Typography.Paragraph>
    </>
  );
};

const ApplicationInfo = ({ nextStep, applicationComplete }) => {
  return (
    <>
      <Row justify="center" gutter={20}>
        <Col xs={24} md={8}>
          <Card
            style={{ textAlign: "center", height: "100%" }}
            title={
              <>
                <Typography.Title
                  level={2}
                  style={{ margin: "0" }}
                  // type="success"
                >
                  <FontAwesomeIcon
                    icon={applicationComplete ? faCheckCircle : faCircle1}
                  />
                </Typography.Title>
              </>
            }
          >
            <Typography.Paragraph>
              Complete your online application - it only takes a few minutes!
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            style={{ textAlign: "center", height: "100%" }}
            title={
              <>
                <Typography.Title level={2} style={{ margin: "0" }}>
                  <FontAwesomeIcon icon={faCircle2} />
                </Typography.Title>
              </>
            }
          >
            <Typography.Paragraph>
              If your application is accepted we will reach out to schedule a 15
              minute virtual screening.
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card
            style={{ textAlign: "center", height: "100%" }}
            title={
              <>
                <Typography.Title
                  level={2}
                  style={{ margin: "0" }}
                  // type="success"
                >
                  <FontAwesomeIcon icon={faCircle3} />
                </Typography.Title>
              </>
            }
          >
            <Typography.Paragraph>
              Complete a background check within 30 days of becoming a caregiver
              <sup>1</sup>.
            </Typography.Paragraph>
          </Card>
        </Col>
      </Row>
      <Row
        gutter={20}
        style={{ textAlign: "center", marginTop: "20px" }}
        align="middle"
      >
        <Col span={24}>
          <Typography.Paragraph type="secondary">
            You must be a US citizen, 18 years or older.
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            <sup>1</sup> Not required for <strong>Career Service</strong>{" "}
            providers.
          </Typography.Paragraph>
          <Button type="primary" size="large" onClick={() => nextStep()}>
            Begin Application
          </Button>
        </Col>
      </Row>
    </>
  );
};

const AppliationWizard = ({ nextStep, initialData }) => {
  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [form] = Form.useForm();

  const caregiver = useCaregiverMutation();

  const onFinish = () => {
    setSubmitting(true);
    const {
      CareType,
      AgeGroups,
      Categories,
      FirstName,
      LastName,
      Timezone,
      Experience,
      Sunday,
      Monday,
      Tuesday,
      Wednesday,
      Thursday,
      Friday,
      Saturday,
      Rate,
      State,
      Phone,
      Email,
    } = form.getFieldsValue(true);

    caregiver
      .mutateAsync({
        CareType: CareType,
        AgeGroups: AgeGroups,
        FirstName: FirstName,
        LastName: LastName,
        Timezone: Timezone,
        Experience: Experience,
        Categories: Categories,
        Availability: [
          Sunday,
          Monday,
          Tuesday,
          Wednesday,
          Thursday,
          Friday,
          Saturday,
        ],
        Rate: Rate,
        State: State,
        Phone: Phone,
        Email: Email,
      })
      .then(() => nextStep())
      .finally(() => setSubmitting(false));
  };

  const advance = async () => {
    // Form validation
    let v = await form.validateFields();

    if (current < items.length - 1) {
      setCurrent(current + 1);
    } else {
      form.submit();
    }
  };

  const SubmitButton = () => {
    return (
      <Button type="primary" onClick={() => advance()} disabled={submitting}>
        Submit
      </Button>
    );
  };

  const AdvanceButton = () => {
    return (
      <Button onClick={() => advance()}>
        <Space>
          Next <FontAwesomeIcon icon={faArrowRight} />
        </Space>
      </Button>
    );
  };

  const FormSection = ({ title, description, instructions, children }) => {
    return (
      <>
        <Row gutter={20} style={{ marginBottom: "40px" }}>
          <Col span={24}>
            <Typography.Title level={4}>{title}</Typography.Title>
            {description && (
              <Typography.Paragraph type="secondary">
                {description}
              </Typography.Paragraph>
            )}
            {instructions && (
              <Typography.Paragraph type="secondary">
                {instructions}
              </Typography.Paragraph>
            )}
          </Col>
          {children}
        </Row>
      </>
    );
  };

  const formItemRules = [
    {
      required: true,
    },
  ];

  const availabilityRules = [
    {
      validator: (_, value) => {
        if (value.valid === false) {
          return Promise.reject();
        }

        return Promise.resolve();
      },
      message: "Your start time must be earlier than your end time",
    },
  ];

  const PersonalInfo = () => {
    return (
      <>
        <FormSection title="Personal Info">
          <Col xs={24} md={8}>
            <Form.Item
              name="FirstName"
              label="First Name"
              rules={formItemRules}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="LastName" label="Last Name" rules={formItemRules}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="Birthday" label="Birthday" rules={formItemRules}>
              <DatePicker
                placeholder="MM/DD/YYYY"
                disabledDate={disabledBirthdays}
                style={{ display: "block", width: "100%" }}
                format="MM/DD/YYYY"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="Phone" label="Phone" rules={formItemRules}>
              <MaskedInput mask={"(000) 000-0000"} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="State" label="State" rules={formItemRules}>
              <Select
                options={stateList.map((s) => {
                  return { label: s, value: s };
                })}
                showSearch
              />
            </Form.Item>
          </Col>
          {/* <Col xs={24} md={8}>
            <Form.Item name="Timezone" label="Timezone" rules={formItemRules}>
              <Select
                options={Intl.supportedValuesOf("timeZone").map((t) => ({
                  label: t,
                  value: t,
                }))}
                showSearch
              />
            </Form.Item>
          </Col> */}
        </FormSection>
      </>
    );
  };

  // Type, Categories, Age
  const CareType = () => {
    const [careType, setCareType] = useState();
    const marks = {
      3: "3",
      19: "18+",
    };

    const formatter = (value) => `${value} years`;
    return (
      <>
        <FormSection
          title="Care Type"
          description="The type of care and categories of service that you will offer"
        >
          <Col xs={24} md={8}>
            <Form.Item name="CareType" label="Care Type" rules={formItemRules}>
              <Select
                options={careCategorySelectOptions(false)}
                onChange={(e) => setCareType(e)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item
              name="Categories"
              label="Categories"
              rules={formItemRules}
            >
              <Select
                mode="tags"
                options={careCategoryOptions(false, careType)}
                maxTagCount={5}
              />
            </Form.Item>
          </Col>
        </FormSection>

        <FormSection
          title="Rate & Age Range"
          description="Your hourly rate, and age range of children you are comfortable working with"
        >
          <Col xs={24}>
            <Form.Item
              name="Rate"
              label="Hourly Rate"
              extra="You can change this later"
              rules={formItemRules}
            >
              <InputNumber
                min={10}
                max={60}
                addonBefore={"$"}
                addonAfter={"/hr"}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="AgeGroups"
              label="Age Range"
              extra="The range of ages you are comfortable providing care for"
              rules={formItemRules}
            >
              <Checkbox.Group options={ageGroupOptions} />
            </Form.Item>
          </Col>
        </FormSection>
      </>
    );
  };

  const Availability = () => {
    return (
      <>
        <FormSection
          title="Availability"
          description="Set the days and hours that parents can schedule time with you"
        >
          <Col xs={24} md={12}>
            <Form.Item name="Sunday" label="Sunday" rules={availabilityRules}>
              <DayAvailability />
            </Form.Item>
            <Form.Item name="Monday" label="Monday" rules={availabilityRules}>
              <DayAvailability />
            </Form.Item>
            <Form.Item name="Tuesday" label="Tuesday" rules={availabilityRules}>
              <DayAvailability />
            </Form.Item>
            <Form.Item
              name="Wednesday"
              label="Wednesday"
              rules={availabilityRules}
            >
              <DayAvailability />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="Thursday"
              label="Thursday"
              rules={availabilityRules}
            >
              <DayAvailability />
            </Form.Item>
            <Form.Item name="Friday" label="Friday" rules={availabilityRules}>
              <DayAvailability />
            </Form.Item>
            <Form.Item
              name="Saturday"
              label="Saturday"
              rules={availabilityRules}
            >
              <DayAvailability />
            </Form.Item>
          </Col>
        </FormSection>
      </>
    );
  };

  const Experience = () => {
    return (
      <FormSection
        title="Experience"
        description="Provide any relevant experience that you have in child care"
        instructions="This information will be visible by parents on Carisity - please write in complete sentences, check your punctuation and grammer, and make your personality come through!"
      >
        <Col span={24}>
          <Form.Item
            name="Experience"
            label="Experience"
            rules={[
              { required: true },
              { min: 200, message: "Please provide at least 200 characters." },
            ]}
          >
            <Input.TextArea
              maxLength={500}
              minLength={200}
              showCount
              rows={8}
            />
          </Form.Item>
        </Col>
      </FormSection>
    );
  };

  const ReviewForm = () => {
    let data = form.getFieldsValue(true);
    const ReviewItem = ({
      name,
      xs = { span: 25 },
      md = { span: 8 },
      children,
    }) => {
      return (
        <Col xs={xs} md={md}>
          {name}: {children}
        </Col>
      );
    };

    return (
      <>
        <FormSection title="Personal Info">
          <ReviewItem name="First Name">{data?.FirstName}</ReviewItem>
          <ReviewItem name="Last Name">{data?.LastName}</ReviewItem>
          <ReviewItem name="Birthday">
            {data?.Birthday?.format("MMM Do YYYY")}
          </ReviewItem>
          <ReviewItem name="Phone">{data?.Phone}</ReviewItem>
          <ReviewItem name="State">{data?.State}</ReviewItem>
          <ReviewItem name="Timezone">{data?.Timezone}</ReviewItem>
        </FormSection>
        <FormSection title="Care Info">
          <ReviewItem name="Care Type">{data?.CareType}</ReviewItem>
          <ReviewItem name="Categories">
            {data.Categories?.join(", ")}
          </ReviewItem>
          <ReviewItem name="Hourly Rate">{data?.Rate}</ReviewItem>
          <ReviewItem name="Age Range">
            {data?.AgeGroups && (
              <>
                {data?.AgeGroups[0]} - {data?.AgeGroups[1]} years
              </>
            )}
          </ReviewItem>
        </FormSection>
        <FormSection title="Availability">
          {data?.Sunday?.Enabled && (
            <ReviewItem name="Sunday">
              {data.Sunday.Start} - {data.Sunday.End}
            </ReviewItem>
          )}
          {data?.Monday?.Enabled && (
            <ReviewItem name="Monday">
              {data.Monday.Start} - {data.Monday.End}
            </ReviewItem>
          )}
          {data?.Tuesday?.Enabled && (
            <ReviewItem name="Tuesday">
              {data.Tuesday.Start} - {data.Tuesday.End}
            </ReviewItem>
          )}
          {data?.Wednesday?.Enabled && (
            <ReviewItem name="Wednesday">
              {data.Wednesday.Start} - {data.Wednesday.End}
            </ReviewItem>
          )}
          {data?.Thursday?.Enabled && (
            <ReviewItem name="Thursday">
              {data.Thursday.Start} - {data.Thursday.End}
            </ReviewItem>
          )}
          {data?.Friday?.Enabled && (
            <ReviewItem name="Friday">
              {data.Friday.Start} - {data.Friday.End}
            </ReviewItem>
          )}
          {data?.Saturday?.Enabled && (
            <ReviewItem name="Saturday">
              {data.Saturday.Start} - {data.Saturday.End}
            </ReviewItem>
          )}
        </FormSection>
        <FormSection title="Experience">
          <ReviewItem name="Experience" md={24}>
            {data?.Experience}
          </ReviewItem>
        </FormSection>
      </>
    );
  };

  const disabledBirthdays = (current) => {
    return current && current > dayjs().subtract(18, "years");
  };

  const items = [
    {
      title: "Personal Info",
      content: <PersonalInfo />,
      advance: <AdvanceButton />,
    },
    {
      title: "Care Information",
      content: <CareType />,
      advance: <AdvanceButton />,
    },
    {
      title: "Availability",
      content: <Availability />,
      advance: <AdvanceButton />,
    },
    {
      title: "Experience",
      content: <Experience />,
      advance: <AdvanceButton />,
    },
    { title: "Review", content: <ReviewForm />, advance: <SubmitButton /> },
  ];

  return (
    <>
      <Row gutter={20}>
        <Col span={24}>
          <Steps
            current={current}
            size="small"
            items={items}
            labelPlacement="vertical"
          />
        </Col>
        <Col span={24}>
          <Form
            initialValues={initialData}
            layout="vertical"
            style={{ padding: "20px 0" }}
            form={form}
            onFinish={() => onFinish()}
          >
            <Card>{items[current].content}</Card>
          </Form>
        </Col>
        <Col span={12}>
          <Button
            onClick={() => setCurrent(current - 1)}
            disabled={current === 0}
            type="text"
          >
            <Space>
              <FontAwesomeIcon icon={faArrowLeft} />
              Previous
            </Space>
          </Button>
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          {items[current].advance}
        </Col>
      </Row>
    </>
  );
};

export default CaregiverApplication;
